// Merchant 合约接口层。
// 业务规则以同目录 interfaces.md、manifest.json 和 abi/ 为准。
// 合约地址使用 network.js；token 定义复用 ../contract。
// 合约实例、Multicall、金额转换复用 ../../ethers/e。
// 钱包余额由统一 BalanceContext 提供，本模块不重复聚合查询。

import MA_ABI from './abi/MA_V2.json'
import MANAGER_ABI from './abi/MerchantManager.json'
import SWAP_ABI from './abi/MerchantSwap.json'
import { MA_V2, MERCHANT_MANAGER, MERCHANT_SWAP, AMOUNT_DECIMALS, CHAIN_ID } from './network'
import { MultiCallAddress, USDT, USD, USD2, CVCD, CVC } from '../contract'
import { isETHAddress } from '../../utils'
import {
    CreateContract,
    GetGlobalSigner,
    MultiCall,
    WeiToUnits,
    ZERO_ADDRESS,
    Erc20,
    MAX256,
    BN
} from '../../ethers/e'

function MAContract() {
    return CreateContract(MA_V2, MA_ABI, GetGlobalSigner())
}

function ManagerContract() {
    return CreateContract(MERCHANT_MANAGER, MANAGER_ABI, GetGlobalSigner())
}

function SwapContract() {
    return CreateContract(MERCHANT_SWAP, SWAP_ABI, GetGlobalSigner())
}

function Multi() {
    return MultiCall(MultiCallAddress, GetGlobalSigner())
}

const MERCHANT_TYPE = {
    blockTimestamp: 0,
    sponsor: ZERO_ADDRESS,
    identities: {
        member: { expiresAt: 0, active: null },
        bole: { expiresAt: 0, active: null },
        merchant: { expiresAt: 0, active: null }
    },
    position: {
        lockedAmount: '0',
        availableAmount: '0',
        settledAt: 0,
        activeUntil: 0,
        active: null,
        withdrawnTotal: '0'
    },
    principal: '0', // USD deposited
    usdtPrincipal: '0', // Canonical Swap exchange cost
    directFeeCapacity: '0',
    rewards: {
        investment: { totalAmount: '0', claimedAmount: '0' },
        promotion: { totalAmount: '0', claimedAmount: '0' },
        directFee: { totalAmount: '0', claimedAmount: '0' }
    }
}

function normalizeRewardLedger(ledger) {
    return {
        totalAmount: WeiToUnits(ledger.totalAmount, AMOUNT_DECIMALS),
        claimedAmount: WeiToUnits(ledger.claimedAmount, AMOUNT_DECIMALS)
    }
}

/**
 * 查询商户身份、仓位与权益账本，不包含钱包 token 余额。
 * 金额为代币单位的十进制字符串，时间为 Unix 秒。
 * active 以链上判断为准；只有 position.availableAmount 是当前可领取额。
 * 查询失败直接抛出，调用方必须区分错误、加载状态与真实零值。
 */
export async function MerchantData(account) {
    const ma = MAContract()
    const manager = ManagerContract()
    const multi = Multi()
    const results = await multi.all({
        blockTimestamp: multi.$.calls.getCurrentBlockTimestamp(),
        sponsor: ma.calls.sponsor(account),
        identity: manager.calls.getMerchantIdentity(account),
        memberActive: manager.calls.hasMerchantIdentity(account, 1),
        boleActive: manager.calls.hasMerchantIdentity(account, 2),
        merchantActive: manager.calls.hasMerchantIdentity(account, 3),
        position: manager.calls.getUsd2Position(account),
        positionActive: manager.calls.isUsd2PositionActive(account),
        withdrawnTotal: manager.calls.usd2Withdrawn(account),
        principal: manager.calls.getCost(account, 103),
        usdtPrincipal: SwapContract().calls.getCost(account, 104),
        directFeeCapacity: manager.calls.getDirectFeeCapacity(account),
        investment: manager.calls.getRewardLedger(account, 200),
        promotion: manager.calls.getRewardLedger(account, 201),
        directFee: manager.calls.getRewardLedger(account, 202)
    })

    return {
        blockTimestamp: BN(results.blockTimestamp).toNumber(),
        sponsor: results.sponsor,
        identities: {
            member: {
                expiresAt: BN(results.identity.userExpiry).toNumber(),
                active: results.memberActive
            },
            bole: {
                expiresAt: BN(results.identity.boleExpiry).toNumber(),
                active: results.boleActive
            },
            merchant: {
                expiresAt: BN(results.identity.merchantExpiry).toNumber(),
                active: results.merchantActive
            }
        },
        position: {
            lockedAmount: WeiToUnits(results.position.lockedAmount, AMOUNT_DECIMALS),
            availableAmount: WeiToUnits(results.position.availableAmount, AMOUNT_DECIMALS),
            settledAt: BN(results.position.settledAt).toNumber(),
            activeUntil: BN(results.position.activeUntil).toNumber(),
            active: results.positionActive,
            withdrawnTotal: WeiToUnits(results.withdrawnTotal, AMOUNT_DECIMALS)
        },
        principal: WeiToUnits(results.principal, AMOUNT_DECIMALS),
        usdtPrincipal: WeiToUnits(results.usdtPrincipal, AMOUNT_DECIMALS),
        directFeeCapacity: WeiToUnits(results.directFeeCapacity, AMOUNT_DECIMALS),
        rewards: {
            investment: normalizeRewardLedger(results.investment),
            promotion: normalizeRewardLedger(results.promotion),
            directFee: normalizeRewardLedger(results.directFee)
        }
    }
}
MerchantData.Type = MERCHANT_TYPE

const MERCHANT_SWAP_TYPE = {
    withdrawalQuota: {
        totalAvailable: '0',
        freeAvailable: '0'
    },
    feeCvcAllowance: '0',
    directFeeAllowance: '0'
}

/**
 * 金额均为 18 位代币单位转换后的字符串，查询失败向上抛出。
 * withdrawalQuota 为 USD2 毛额，freeAvailable 已包含在 totalAvailable 内。
 * feeCvcAllowance 为有效手续费资格余量，不是可领取的 CVC 数量。
 * directFeeAllowance 才是结合累计 USDT 投入限制后实际可获的直推手续费奖励额度。
 */
export async function MerchantSwapData(account) {
    const swap = SwapContract()
    const results = await Multi().all({
        withdrawalQuota: swap.calls.getWithdrawalQuota(account),
        feeCvcAllowance: swap.calls.getFeeCvcAllowance(account),
        directFeeAllowance: swap.calls.getDirectFeeAllowance(account)
    })

    return {
        withdrawalQuota: {
            totalAvailable: WeiToUnits(results.withdrawalQuota.totalAvailable, AMOUNT_DECIMALS),
            freeAvailable: WeiToUnits(results.withdrawalQuota.freeAvailable, AMOUNT_DECIMALS)
        },
        feeCvcAllowance: WeiToUnits(results.feeCvcAllowance, AMOUNT_DECIMALS),
        directFeeAllowance: WeiToUnits(results.directFeeAllowance, AMOUNT_DECIMALS)
    }
}
MerchantSwapData.Type = MERCHANT_SWAP_TYPE

const MERCHANT_STAKE_TYPE = {
    blockTimestamp: 0,
    totalStaked: '0',
    totalLocked: '0',
    totalClaimable: '0',
    orders: []
}

/**
 * 三类质押独立编号，以 source + id 唯一定位订单；保留已领取历史订单。
 * totalStaked 是全部未领取 CVC（包含到期部分），totalLocked 仅含未到期部分。
 * 金额为代币单位字符串，时间与剩余时长均为秒；到期一次性领取，不计算线性释放。
 */
export async function MerchantStakeData(account) {
    const swap = SwapContract()
    const manager = ManagerContract()
    const multi = Multi()
    const results = await multi.all({
        swap: swap.calls.getCvcStake(account),
        swapFee: swap.calls.getFeeCvcStake(account),
        manager: manager.calls.getCvcStake(account),
        blockTimestamp: multi.$.calls.getCurrentBlockTimestamp()
    })
    const blockTimestamp = BN(results.blockTimestamp).toNumber()
    let totalStaked = BN(0)
    let totalLocked = BN(0)
    let totalClaimable = BN(0)
    const orders = []

    for (const source of ['swap', 'swapFee', 'manager']) {
        for (const [id, order] of results[source].entries()) {
            const unlockTime = BN(order.unlockTime).toNumber()
            const matured = blockTimestamp >= unlockTime
            const claimable = !order.withdrawn && matured && order.amount.gt(0)

            if (!order.withdrawn) {
                totalStaked = totalStaked.add(order.amount)
                if (matured) {
                    totalClaimable = totalClaimable.add(order.amount)
                } else {
                    totalLocked = totalLocked.add(order.amount)
                }
            }

            orders.push({
                source,
                id,
                key: `${source}:${id}`,
                amount: WeiToUnits(order.amount, AMOUNT_DECIMALS),
                unlockTime,
                withdrawn: order.withdrawn,
                claimable,
                remainingSeconds: Math.max(0, unlockTime - blockTimestamp),
                // ABI 旧字段名，不代表 USDT 钱包余额或当前可获奖励额度。
                // 实际手续费奖励额度应读 MerchantSwapData.directFeeAllowance。
                ...(source === 'swapFee' ? {
                    remainingUsdt: WeiToUnits(order.remainingUsdt, AMOUNT_DECIMALS)
                } : {})
            })
        }
    }

    return {
        blockTimestamp,
        totalStaked: WeiToUnits(totalStaked, AMOUNT_DECIMALS),
        totalLocked: WeiToUnits(totalLocked, AMOUNT_DECIMALS),
        totalClaimable: WeiToUnits(totalClaimable, AMOUNT_DECIMALS),
        orders
    }
}
MerchantStakeData.Type = MERCHANT_STAKE_TYPE

/**
 * 输入数量为最小单位整数（BigNumber 或整数字符串），由 hook 转换用户输入。
 * amountUsd2 对应 ABI 的 amountUSD，实际输入代币为 USD2。
 * 输出为代币单位字符串；实际 CVCD 用量和身份倍数以合约报价为准。
 * 报价不锁定成交结果，也不保证库存充足；读取失败直接向上抛出。
 */
export async function QuoteBuyUsd(account, amountUsdt, amountCvcd, amountUsd2) {
    const swap = SwapContract()
    const amounts = purchaseAmounts(amountUsdt, amountCvcd, amountUsd2)
    const quote = await swap.quoteUsd(account, ...amounts)
    return {
        cvcdUsed: WeiToUnits(quote.cvcdUsed, AMOUNT_DECIMALS),
        usdReceived: WeiToUnits(quote.usdReceived, AMOUNT_DECIMALS)
    }
}

/**
 * amount 为 USD2 毛额的最小单位整数，输出为各代币单位的十进制字符串。
 * freeUsed 仅免除对应的新 CVC 质押，不免手续费。
 * feeUsdt 是名义手续费，不代表全部实际支付为 USDT。
 * 舍入、额度与所需 CVC 均以合约为准；超额等读取错误直接向上抛出。
 * 报价不保证后续交易成功，提交前需重新查询。
 */
export async function QuoteRedeemUsd2(account, amount) {
    const swap = SwapContract()
    const quote = await swap.quoteUsd2Redemption(account, amount)
    return {
        freeUsed: WeiToUnits(quote.freeUsed, AMOUNT_DECIMALS),
        cvcRequired: WeiToUnits(quote.cvcRequired, AMOUNT_DECIMALS),
        feeUsdt: WeiToUnits(quote.feeUsdt, AMOUNT_DECIMALS),
        netUsdt: WeiToUnits(quote.netUsdt, AMOUNT_DECIMALS),
        reinvestedUsd2: WeiToUnits(quote.reinvestedUsd2, AMOUNT_DECIMALS)
    }
}

function merchantError(code, message) {
    return Object.assign(new Error(message), { code })
}

// 交易前单独复核绑定关系，避免为推荐人读取整套商户数据。
export function MerchantSponsor(account) {
    return MAContract().sponsor(account)
}

/**
 * token 使用 contract.js 的代币数组，amountWei 为最小单位整数。
 * 仅支持当前 merchant 业务的 token/spender 配对，使用真实 signer 地址查额度。
 * 跳过时返回 { approved: false, tx: null, receipt: null }。
 * 实际授权会等待确认，返回 { approved: true, tx, receipt }；不使用无限授权。
 * 非零但不足的额度先清零并确认，再设置所需额度。失败直接抛出，不自动重试。
 */
export async function EnsureMerchantAllowance(token, spender, amountWei) {
    const spenderKey = typeof spender === 'string' ? spender.toLowerCase() : ''
    const allowedTokens = spenderKey === MERCHANT_MANAGER.toLowerCase()
        ? [USDT, USD, CVC]
        : spenderKey === MERCHANT_SWAP.toLowerCase() ? [USDT, CVCD, USD2, CVC] : []
    const tokenAddress = Array.isArray(token) && typeof token[0] === 'string' ? token[0] : ''
    if (!allowedTokens.some(item => item[0].toLowerCase() === tokenAddress.toLowerCase())) {
        throw merchantError('INVALID_MERCHANT_APPROVAL', 'Unsupported merchant token or spender.')
    }
    const amount = BN(amountWei)
    if (amount.lt(0) || amount.gt(MAX256)) {
        throw merchantError('INVALID_AMOUNT', 'Approval amount is outside uint256 range.')
    }
    const skipped = { approved: false, tx: null, receipt: null }
    if (amount.isZero()) return skipped

    const signer = GetGlobalSigner()
    if (!signer || typeof signer.getAddress !== 'function' || typeof signer.getChainId !== 'function') {
        throw merchantError('WALLET_REQUIRED', 'Connect a wallet to approve tokens.')
    }
    const owner = await signer.getAddress()
    async function checkWallet() {
        const [chainId, currentOwner] = await Promise.all([signer.getChainId(), signer.getAddress()])
        if (chainId !== CHAIN_ID) {
            throw merchantError('MERCHANT_NETWORK_MISMATCH', 'Select the configured merchant network.')
        }
        if (GetGlobalSigner() !== signer || currentOwner.toLowerCase() !== owner.toLowerCase()) {
            throw merchantError('MERCHANT_ACCOUNT_CHANGED', 'Wallet changed. Please retry.')
        }
    }
    await checkWallet()
    const erc20 = Erc20(tokenAddress, signer)
    const decimals = Number(await erc20.decimals())
    if (decimals !== AMOUNT_DECIMALS || decimals !== token[1]) {
        throw merchantError('MERCHANT_TOKEN_DECIMALS_MISMATCH', 'Token decimals do not match the merchant configuration.')
    }
    const allowance = await erc20.allowance(owner, spender)
    if (allowance.gte(amount)) return skipped

    async function approve(value) {
        await checkWallet()
        const tx = await erc20.approve(spender, value)
        const receipt = await tx.wait()
        if (!receipt || receipt.status !== 1) {
            throw merchantError('MERCHANT_APPROVAL_FAILED', 'Token approval was not successful.')
        }
        return { tx, receipt }
    }
    if (allowance.gt(0)) await approve(BN(0))
    const { tx, receipt } = await approve(amount)
    return { approved: true, tx, receipt }
}

/**
 * identity 只接受数字 1（普通用户）、2（伯乐）、3（链商）。
 * @<merchant-identity-fees> Paid identity fees are 1/1/5 USDT; router amount remains zero.
 * hook 负责网络/账户、推荐人绑定检查，以及向 Manager 授权 USDT。
 * 此处只提交 MA 交易并返回 tx，不等待确认或自动重试。
 */
export async function BuyMerchantIdentity(identity, sponsor) {
    if (![1, 2, 3].includes(identity)) {
        throw merchantError('INVALID_MERCHANT_IDENTITY', 'Select a supported merchant identity.')
    }
    if (!isETHAddress(sponsor) || sponsor.toLowerCase() === ZERO_ADDRESS) {
        throw merchantError('INVALID_MERCHANT_SPONSOR', 'Provide a valid nonzero sponsor address.')
    }
    return MAContract().router(identity, sponsor, 0)
}

/**
 * amount 为 USD 最小单位整数（不是 USD2，也不再乘 10^18）。
 * hook 负责绑定检查和 USD -> Manager 授权；实际交易发往 MA 的 action 4。
 * 返回未确认的 tx，失败向上抛出；入金增加权益，不是立即到账的钱包 USD2。
 */
export async function DepositUsd(amount, sponsor) {
    let amountWei
    try {
        amountWei = BN(amount)
    } catch {
        throw merchantError('INVALID_AMOUNT', 'Deposit amount must be an integer in smallest units.')
    }
    if (amountWei.lte(0) || amountWei.gt(MAX256)) {
        throw merchantError('INVALID_AMOUNT', 'Deposit amount must be positive and within uint256 range.')
    }
    if (!isETHAddress(sponsor) || sponsor.toLowerCase() === ZERO_ADDRESS) {
        throw merchantError('INVALID_MERCHANT_SPONSOR', 'Provide a valid nonzero sponsor address.')
    }
    return MAContract().router(4, sponsor, amountWei)
}

/**
 * 领取调用者已释放的全部 USD2，不需授权、不传金额或收款地址。
 * 返回未确认的 tx；实际领取量以确认后的 Usd2Withdrawn 事件为准。
 * 无可领取额、库存不足或用户拒绝等错误向上抛出，不自动重试。
 */
export function WithdrawUsd2() {
    return ManagerContract().withdrawUsd2()
}

/**
 * 三个数量均为最小单位整数；第三个参数是 USD2（ABI 旧名 amountUSD）。
 * @<merchant-cvcd-40-percent> Accept pure USDT or USDT plus floor(USDT*2/5) CVCD; the third input must be zero.
 * hook 负责报价及向 Swap 授权，CVCD 仅授权报价实际用量。
 * 返回未确认的 tx，实际 USD 到账以 UsdPurchased 事件为准。
 */
function purchaseAmounts(amountUsdt, amountCvcd, amountUsd2) {
    let amounts
    try {
        amounts = [amountUsdt, amountCvcd, amountUsd2].map(value => BN(value))
    } catch {
        throw merchantError('INVALID_AMOUNT', 'Purchase amounts must be integers in smallest units.')
    }
    if (amounts.some(value => value.lt(0) || value.gt(MAX256))) {
        throw merchantError('INVALID_AMOUNT', 'Purchase amounts must be within uint256 range.')
    }
    if (amounts[0].isZero()) {
        throw merchantError('INVALID_AMOUNT', 'Provide a positive USDT amount; USD2 purchases are disabled.')
    }
    if (!amounts[2].isZero() || (!amounts[1].isZero() && !amounts[1].eq(amounts[0].mul(2).div(5)))) {
        throw merchantError('INVALID_PAYMENT_COMBINATION', 'Use USDT alone or USDT with floor(USDT * 2 / 5) CVCD, without USD2.')
    }
    return amounts
}

// All purchases use MA actions 5/6; direct Swap and USD2 purchases are disabled.
// An existing sponsor is read when omitted; an unbound user must explicitly supply one.
export async function BuyUsd(amountUsdt, amountCvcd, amountUsd2, sponsor) {
    const amounts = purchaseAmounts(amountUsdt, amountCvcd, amountUsd2)
    const resolved = sponsor === undefined
        ? await MerchantSponsor(await GetGlobalSigner().getAddress()) : sponsor
    if (!isETHAddress(resolved) || BN(resolved).lte(1)) {
        throw merchantError('INVALID_MERCHANT_SPONSOR', 'Provide a valid sponsor for this USDT purchase.')
    }
    return MAContract().router(amounts[1].isZero() ? 5 : 6, resolved, amounts[0])
}

function positiveAmount(amount) {
    let value
    try { value = BN(amount) } catch {
        throw merchantError('INVALID_AMOUNT', 'Amount must be an integer in smallest units.')
    }
    if (value.lte(0) || value.gt(MAX256)) {
        throw merchantError('INVALID_AMOUNT', 'Amount must be positive and within uint256 range.')
    }
    return value
}

function stakeOrderId(id) {
    let value
    try { value = BN(id) } catch {
        throw merchantError('INVALID_STAKE_ORDER', 'Invalid stake order ID.')
    }
    if (value.lt(0) || value.gt(MAX256)) {
        throw merchantError('INVALID_STAKE_ORDER', 'Invalid stake order ID.')
    }
    return value
}

// amount 为 USD2 毛额；hook 向 Swap 授权 USD2 与报价所需的新 CVC。
export async function RedeemUsd2(amount) {
    return SwapContract().redeemUsd2(positiveAmount(amount))
}

// 等量 CVCD 销毁、CVC 锁定；两种代币均授权给 Swap。
export async function StakeFeeCvc(amount) {
    return SwapContract().stakeFeeCvc(positiveAmount(amount))
}

// Manager 独立质押，不抵扣 Swap 兑换要求的新 CVC，授权给 Manager。
export async function StakeManagerCvc(amount) {
    return ManagerContract().stakeCvc(positiveAmount(amount))
}

// 显式选用单笔重载，绝不将缺失的 id 解释为领取全部。
export async function UnstakeSwapCvc(id) {
    return SwapContract()['unstakeCvc(uint256)'](stakeOrderId(id))
}

export async function UnstakeFeeCvc(id) {
    return SwapContract()['unstakeFeeCvc(uint256)'](stakeOrderId(id))
}

export async function UnstakeManagerCvc(id) {
    return ManagerContract()['unstakeCvc(uint256)'](stakeOrderId(id))
}

const MERCHANT_FEE_TYPE = {
    treasury: '0', dao: '0', zeroLine: '0', community: '0', safetyVault: '0'
}

// 实际已支付 USDT，排除旧直推 category 2；直推手续费权益另读 Manager reward202。
export async function MerchantFeeData(account) {
    const swap = SwapContract()
    const results = await Multi().all({
        treasury: swap.calls.feeUsdtPaid(account, 1),
        dao: swap.calls.feeUsdtPaid(account, 3),
        zeroLine: swap.calls.feeUsdtPaid(account, 4),
        community: swap.calls.feeUsdtPaid(account, 5),
        safetyVault: swap.calls.feeUsdtPaid(account, 6)
    })
    return Object.fromEntries(Object.keys(MERCHANT_FEE_TYPE).map(key => [key, WeiToUnits(results[key], AMOUNT_DECIMALS)]))
}
MerchantFeeData.Type = MERCHANT_FEE_TYPE

// 显式批量入口：由合约筛选当前调用者全部到期且未领取的订单。
export async function UnstakeAllSwapCvc() {
    return SwapContract()['unstakeCvc()']()
}
export async function UnstakeAllFeeCvc() {
    return SwapContract()['unstakeFeeCvc()']()
}
export async function UnstakeAllManagerCvc() {
    return ManagerContract()['unstakeCvc()']()
}
